<template>
  <div class="f-w f-h user">
    <Tabs v-model="focusedTab" :tabs="tabs">

      <!-- User account -->
      <div v-if="focusedTab === 0" class="user-account">
        <!-- Title and picture -->
        <div class="user-account-header">
          <UserThumbnail
            :value="currentUser.id"
            class="user-account-header__picture"
            :all-users="[currentUser]"
            disabled
            fetch-data
            edit-picture-on-click
          />
          <h2 class="basic-sub-title">
            {{ $t('message.USER_ACCOUNT') }}
          </h2>
        </div>

        <div class="user-account-content">
          <div class="user-account-content-top-wrapper">

            <!-- Basic user deails (names, email) -->
            <div class="user-account-content__basic">
              <EditableLabel
                :value="currentUser.lastName"
                :legend="`${$t('message.NAME')}${$t('message.TWO_DOTS')}`"
                class="user-account-content__field clickable-hoverable"
                :disabled="disabled"
                @input="editUser('lastName', $event)"
              />
              <EditableLabel
                :value="currentUser.firstName"
                :legend="`${$t('message.FIRST_NAME')}${$t('message.TWO_DOTS')}`"
                class="user-account-content__field clickable-hoverable"
                :disabled="disabled"
                @input="editUser('firstName', $event)"
              />
              <EditableLabel
                :value="currentUser.username"
                :legend="`${$t('message.USERNAME')}${$t('message.TWO_DOTS')}`"
                class="user-account-content__field clickable-hoverable"
                :disabled="disabled"
                @input="editUser('username', $event)"
              />
              <EditableLabel
                :value="currentUser.email"
                :legend="`${$t('message.EMAIL_ADDRESS')}${$t('message.TWO_DOTS')}`"
                class="user-account-content__field clickable-hoverable"
                :disabled="disabled"
                @input="editUser('email', $event)"
              />
              <button
                v-if="!disabled"
                class="add-button user-account-content__field clickable-hoverable user-account-content__pwd-button"
                :class="{ 'user-account-content__pwd-button--active': editingPassword }"
                @click="editingPassword = !editingPassword"
              >
                <Icon>{{ keyMedium }}</Icon>
                <span>{{ $t('message.CHANGE_MY_PASSWORD') }}</span>
              </button>
              <TransitionExpand>
                <PwdEditForm
                  v-if="editingPassword && !disabled"
                  focus-on-mount
                  @submit="updatePwd"
                />
              </TransitionExpand>
            </div>

            <!-- User login deails (teams, ref team, current team, password) -->
            <div class="user-account-content__connection">
              <EditableLabel
                v-if="stateUserTeamObject"
                :value="stateUserTeamObject.name"
                :legend="`${$t('message.CONFIG_PREFERRED_TEAM')}${$t('message.TWO_DOTS')}`"
                class="user-account-content__field"
                disabled
              />
              <EditableLabelRadio
                :value="currentUser.teams"
                :legend="`${$t('message.OTHER_TEAMS')}${$t('message.TWO_DOTS')}`"
                :items="allTeams"
                class="user-account-content__field"
                label-key="name"
                multiple
                disabled
              />
              <EditableLabelRadio
                :value="currentUser.lastUsedTeam || currentUser.teamId"
                :legend="`${$t('message.CURRENT_TEAM')}${$t('message.TWO_DOTS')}`"
                :items="allTeams.filter(team => currentUser.teams.includes(team.id))"
                class="user-account-content__field clickable-hoverable"
                label-key="name"
                :disabled="disabled"
                @input="switchUserTeam"
              />
              <div class="user-account-content__field">
                {{ $tc('message.N_MEMBERS', nMembers, { nb: nMembers }) }}
              </div>
            </div>
          </div>

          <!-- Data usage information -->
          <div class="user-account-content__data-usage">
            <div class="field-hint">
              <div>{{ $t('message.DATA_USAGE_SUMMARY') }}</div>
              <ul class="user-account-content__data-usage__list">
                <li>
                  <span class="user-account-content__data-usage__item-title">
                    {{ `${$t('message.DATA_USAGE_NAMES_TITLE')}${$t('message.TWO_DOTS')}` }}
                  </span>
                  <span class="user-account-content__data-usage__item-message">
                    {{ $t('message.DATA_USAGE_NAMES') }}
                  </span>
                </li>
                <li>
                  <span class="user-account-content__data-usage__item-title">
                    {{ `${$t('message.DATA_USAGE_EMAIL_TITLE')}${$t('message.TWO_DOTS')}` }}
                  </span>
                  <span class="user-account-content__data-usage__item-message">
                    {{ $t('message.DATA_USAGE_EMAIL') }}
                  </span>
                </li>
                <li>
                  <span class="user-account-content__data-usage__item-title">
                    {{ `${$t('message.DATA_USAGE_PICTURE_TITLE')}${$t('message.TWO_DOTS')}` }}
                  </span>
                  <span class="user-account-content__data-usage__item-message">
                    {{ $t('message.DATA_USAGE_PICTURE') }}
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <!-- End of user account -->

      <!-- User problems -->
      <Problems
        v-else-if="focusedTab === 1"
        as-component
        hide-header
        all-user-teams
      />
      <!-- End of user problems -->

      <!-- User actions -->
      <div v-else-if="focusedTab === 2">
        <div class="field-hint user__tab-hint">
          {{ $t('message.MY_ACTIONS_HINT') }}
        </div>
        <div
          v-for="plan in actionsAffectedPlans"
          :key="plan.id"
        >
          <h2 class="basic-sub-title">
            {{ plan.name }}
          </h2>
          <PlanActionItem
            v-for="action in plan.actions"
            :key="action.id"
            :action="action"
            :all-users="$store.getters.usersPerTeam[`${action.affectTeam}`]"
            :can-drag="false"
            :plan="plan"
            :n-lines="1"
            @pdcaEdit="refreshActions"
            @leaderEdit="refreshActions"
          />
        </div>
      </div>
      <!-- End of user actions -->

      <!-- Actions created by the current user and sent -->
      <div v-else-if="focusedTab === 3">
        <div class="field-hint user__tab-hint">
          {{ $t('message.MY_SENT_ACTIONS_HINT') }}
        </div>
        <div
          v-for="plan in actionsSentPlans"
          :key="plan.id"
        >
          <h2 class="basic-sub-title">
            {{ plan.name }}
          </h2>
          <PlanActionItem
            v-for="action in plan.actions"
            :key="action.id"
            :action="action"
            :all-users="$store.getters.usersPerTeam[`${action.affectTeam}`]"
            :can-drag="false"
            :plan="plan"
            :n-lines="1"
            @pdcaEdit="refreshActions"
            @leaderEdit="refreshActions"
          />
        </div>
      </div>
      <!-- End of Actions created by the current user and sent -->

    </Tabs>
  </div>
</template>

<script>
/**
 * Account page with details for a user.
 * If no user secified, details for the current user
 */


import toastr from 'toastr';
import { isEqual, cloneDeep } from 'lodash';
// Vuex utils
import { mapState } from 'vuex';
import { GET_TEAMS, GET_USERS } from '@/store/mutation-types';
import { FETCH_TEAMS, FETCH_USERS, SWITCH_USER_TEAM, EDIT_USER } from '@/store/action-types';
// Components
import EditableLabel from '@/components/editable-label.vue';
import EditableLabelRadio from '@/components/editable-label-radio.vue';
import Icon from '@/components/icon.vue';
import PlanActionItem from '@/components/plan-action-item.vue';
import Problems from '@/views/dmp/problems.vue';
import PwdEditForm from '@/components/pwd-edit-form.vue';
import Tabs from '@/components/tabs.vue';
import TransitionExpand from '@/components/transition-expand.vue';
import UserThumbnail from '@/components/user-thumbnail.vue';
// Utils
import { calert } from '@/utils/main';
import { keyMedium } from '@/assets/icons';

export default {

  name: 'User',

  components: {
    EditableLabel,
    EditableLabelRadio,
    Icon,
    PlanActionItem,
    Problems,
    PwdEditForm,
    Tabs,
    TransitionExpand,
    UserThumbnail,
  },

  data() {
    return {
      keyMedium,
      // True if the password div is opened
      editingPassword: false,
      // True if there's an ongoing request to update the pwd
      updatingPassword: false,
      actionPlansPerTeam: {},
    };
  },

  computed: {
    tabs() {
      // TODO after the redesign of tabs, reduce the padding of the tabs titles
      if (this.$store.state.device.isSmartphonePortrait) {
        return [
          this.$t('message.MY_ACCOUNT_SHORT'),
          this.$t('message.PROBLEMS'),
          this.$t('message.ACTIONS'),
          this.$t('message.SENT_INFOS_SHORT'),
        ];
      }
      return [
        this.$t('message.MY_ACCOUNT'),
        this.$t('message.MY_PROBLEMS'),
        this.$t('message.MY_ACTIONS'),
        this.$t('message.MY_SENT_ACTIONS'),
      ];
    },
    /** Number: ID of the user whose infos must be displayed on the page */
    userId() {
      // TODO: Allow displaying other users (using query params)
      return this.pageUserObject.id;
    },
    /** Object: User object to display on this page */
    pageUserObject() {
      // TODO: Allow displaying other users (using query params)
      return this.currentUser;
    },
    /** Boolean: Whether the edition is disabled */
    disabled() {
      return this.userId !== this.currentUser.id;
    },
    /** Object: Team from teams/get.php where id = currentTeam.id */
    stateCurrentTeamObject() {
      return this.allTeams.find(team => team.id === this.currentTeam.id);
    },
    /** Object: Team from teams/get.php where id = currentUser.team */
    stateUserTeamObject() {
      return this.pageUserObject
        ? this.allTeams.find(team => team.id === this.pageUserObject.team)
        : null;
    },
    /** Number: Number of team members */
    nMembers() {
      return this.stateCurrentTeamObject ? this.stateCurrentTeamObject.users.length : 0;
    },
    /** Array: Action plans that contain at least one action where the user is the leader */
    actionPlans() {
      const allPlans = [];
      Object.keys(this.actionPlansPerTeam).forEach((teamId) => {
        const plans = cloneDeep(this.actionPlansPerTeam[teamId]);
        const teamName = this.$store.getters.companyTeams[`${teamId}`].name;
        plans.forEach((plan) => {
          if (plan.actions.length > 0) {
            plan.name = `${plan.name} ${this.$t('message.CONTENT_BETWEEN_PARENTHESES', { content: teamName })}`;
            allPlans.push(plan);
          }
        });
      });
      return allPlans;
    },
    /** Array: Array of action plans containing actions assigned to the current user */
    actionsAffectedPlans() {
      return cloneDeep(this.actionPlans)
        .map((plan => ({
          ...plan,
          actions: plan.actions.filter(action => action.leader === this.currentUser.id),
        })))
        .filter(plan => plan.actions.length > 0);
    },
    /** Array: Array of support action plans containing actions that the current user created and sent */
    actionsSentPlans() {
      return cloneDeep(this.actionPlans)
        .filter(plan => plan.support === 1)
        .map((plan => ({
          ...plan,
          actions: plan.actions.filter(action => action.authorId === this.currentUser.id),
        })))
        .filter(plan => plan.actions.length > 0);
    },
    ...mapState({
      allUsers: state => state[GET_USERS.stateKey] || [],
      allUsersLoading: state => state[GET_USERS.loadingKey],
      allTeams: state => state[GET_TEAMS.stateKey] || [],
      allTeamsLoading: state => state[GET_TEAMS.loadingKey],
    }),
    focusedTab: {
      get() {
        return Number(this.$route.query.userFocusedTab || 0);
      },
      set(tab) {
        const query = Object.assign({}, this.$route.query);
        query.userFocusedTab = Number(tab) % this.tabs.length;
        if (!isEqual(query, this.$route.query)) {
          this.$router.replace({ query });
        }
      },
    },
  },

  watch: {
    focusedTab() {
      this.tabChanged();
    },
  },

  created() {
    if (!this.allTeamsLoading) {
      this.$store.dispatch(FETCH_TEAMS);
    }
    if (!this.allUsersLoading) {
      this.$store.dispatch(FETCH_USERS);
    }
    this.tabChanged();
  },

  methods: {
    tabChanged() {
      if (this.focusedTab === 1) {
        const query = Object.assign({}, this.$route.query);
        query.problemsLeaders = `${this.userId}`;
        if (!isEqual(query, this.$route.query)) {
          this.$router.replace({ query });
        }
      } else if ([2, 3].includes(this.focusedTab)) {
        this.refreshActions();
      }
    },
    /** Change a user property */
    editUser(param, value) {
      this.$store.dispatch(EDIT_USER, { id: this.userId, [param]: value });
    },
    /** Switch between teams and login as another team */
    switchUserTeam(team) {
      this.loading = true;
      this.$store.dispatch(SWITCH_USER_TEAM, { team, resetPage: true }).then(() => {
        this.$nextTick(() => {
          this.loading = false;
        });
      });
    },
    /** Updates the password and sends to server */
    updatePwd(passFormer, passNew, passNewConfirm) {
      if (this.updatingPassword) {
        return;
      }
      const len = passNew.length;
      if (len < 8 || len > 25) {
        calert(this.$t('message.PWD_BETWEEN_8_25'), [{ title: this.$t('message.OK') }]);
        return;
      }
      if (passNewConfirm !== passNew) {
        calert(this.$t('message.PWD_DONT_MATCH'), [{ title: this.$t('message.OK') }]);
        return;
      }
      this.updatingPassword = true;
      // Send to server
      this.$http.post('/server/users/editPwd.php', { former: passFormer, new: passNew })
        .then((response) => {
          this.updatingPassword = false;
          if (response && response.data === 1) {
            calert(this.$t('message.PWD_EDITED'));
            this.editingPassword = false;
          } else {
            toastr.error(this.$t('message.PWD_ERROR_CHECK'));
          }
        })
        .catch(() => {
          this.updatingPassword = false;
        });
    },

    refreshActions() {
      this.$http.post(
        '/server/actionPlans/get.php',
        { teams: this.pageUserObject.teams, userFilter: this.pageUserObject.id, actions: true },
      )
        .then((response) => {
          if (response.data instanceof Object) {
            this.actionPlansPerTeam = response.data;
            return;
          }
          toastr.success(this.$t('message.PWD_EDITED'));
        });
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.user {
  background: white;
  overflow: auto;
  height: 100%;
  width: 100%;
  box-sizing: border-box;

  > .tabs {
    height: 100%;

    > .content-wrapper {
      background: #fcfcfc;
    }
  }

  &-account {
    padding: 0em 1em 4em 1em;

    &-header {
      position: relative;
      text-align: center;
      padding: 1em 0em;

      h2 {
        margin: 0px;
      }
      .user-thumbnail{
        &__avatar {
          width: 5em;
          height: 5em;
        }
      }
    }

    &-content {
      &-top-wrapper {
        display: flex;
      }
      &__basic, &__connection {
        flex: 1;
        min-width: 0px;
      }
      &__field {
        margin: 0px;
        padding: 0.35em;
      }
      &__pwd-button--active {
        background: #eeeeee;
      }
      .pwd-edit-form {
        background: #eeeeee;
        padding: 0.2em 0.8em;
      }

      &__data-usage {
        margin-top: 4.5em;

        &__list {
          margin: 0.5em 2em;
        }
      }
    }
  }

  &__tab-hint {
    margin: 1rem;
    max-width: 450px;
  }
}

@media (max-width: $smartphone-portrait) {
  .user {
    &-content {
      &-top-wrapper {
        flex-flow: column;
      }
    }
  }
}

@media (max-width: $small-smartphone-portrait) {
  .user {
    &-header {
      h2 {
        display: none;
      }
    }
  }
}
</style>
